import {
  SHOPIFY_INSTALL_STEP_PATHS,
  type ShopifyInstallStepPath,
  type ShopifyInstallWizardState,
} from '../types/shopifyInstallWizard';
import {
  readShopifyInstallStorage,
  removeShopifyInstallStorage,
  SHOPIFY_INSTALL_STORAGE_KEYS,
  writeShopifyInstallStorage,
} from './shopifyInstallPersistence';

const STORAGE_KEY = SHOPIFY_INSTALL_STORAGE_KEYS.wizard;

export function readShopifyInstallWizardState(): ShopifyInstallWizardState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = readShopifyInstallStorage(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ShopifyInstallWizardState;
  } catch {
    return null;
  }
}

export function writeShopifyInstallWizardState(
  patch: Partial<ShopifyInstallWizardState>
): ShopifyInstallWizardState {
  const prev = readShopifyInstallWizardState() || { shopDomain: '' };
  const next: ShopifyInstallWizardState = {
    ...prev,
    ...patch,
    shopDomain: patch.shopDomain || prev.shopDomain || '',
  };
  writeShopifyInstallStorage(STORAGE_KEY, JSON.stringify(next));
  return next;
}

export function clearShopifyInstallWizardState(): void {
  try {
    removeShopifyInstallStorage(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export function markWizardStepComplete(step: ShopifyInstallStepPath): void {
  const state = readShopifyInstallWizardState();
  const completed = new Set(state?.completedSteps || []);
  completed.add(step);
  writeShopifyInstallWizardState({ completedSteps: Array.from(completed) });
}

export function shopifyInstallWizardSearch(
  state?: ShopifyInstallWizardState | null
): string {
  const s = state || readShopifyInstallWizardState();
  const q = new URLSearchParams();
  if (s?.shopDomain) q.set('shop', s.shopDomain);
  if (s?.host) q.set('host', s.host);
  const str = q.toString();
  return str ? `?${str}` : '';
}

export function installWizardPath(
  step: ShopifyInstallStepPath,
  state?: ShopifyInstallWizardState | null
): string {
  return `/shopify/install/${step}${shopifyInstallWizardSearch(state)}`;
}

/** Previous step; workspace may be skipped after account for single-workspace merchants. */
export function installWizardBackPath(from: ShopifyInstallStepPath): string {
  const state = readShopifyInstallWizardState();
  const completed = new Set(state?.completedSteps || []);
  if (from === 'goals') {
    return completed.has('workspace')
      ? installWizardPath('workspace')
      : installWizardPath('account');
  }
  const idx = SHOPIFY_INSTALL_STEP_PATHS.indexOf(from);
  if (idx <= 0) {
    return installWizardPath('account');
  }
  return installWizardPath(SHOPIFY_INSTALL_STEP_PATHS[idx - 1]);
}
